import { motion } from "framer-motion";
import {
  Send, Mic, MicOff, Volume2, VolumeX, Maximize2, Minimize2,
  Paperclip, Headphones,
} from "lucide-react";

/**
 * ChatInputBar — barra de entrada do chat com 6 controles + input.
 *
 * Mobile Parity Epic — Etapa 4: extraído do JSX inline que vivia no
 * NectarDashboard.jsx. Renderizado pelo ContextPanel em ambos modos
 * (permanent desktop + bottom-sheet mobile).
 *
 * Fix dimensões (pós-hotfix): ContextPanel é faixa vertical estreita
 * em desktop (384px) e mobile (full-screen). Layout sempre vertical:
 * input full-width na linha 1 + 6 botões w-11 espalhados (justify-between)
 * na linha 2. Botões w-11 (44px — target touch Apple HIG) com ícones w-4.
 *
 * Props:
 *   chat       — objeto com inputText, setInputText, handleSubmit, isLoading
 *   voice      — objeto com voiceEnabled, toggleVoice, wakeWordEnabled,
 *                wakeWordListening, toggleWakeWord, voiceOverlayOpen,
 *                endConversation
 *   isFocusMode + onToggleFocusMode
 *   onMicClick — handler quando user toca o mic (start conversation)
 *   onAttachClick + chatAttachSubmitting — A3 anexo de imagem
 */
export default function ChatInputBar({
  chat,
  voice: v,
  isFocusMode,
  onToggleFocusMode,
  onMicClick,
  onAttachClick,
  chatAttachSubmitting = false,
}) {
  return (
    <div className="p-3">
      <form onSubmit={chat.handleSubmit} className="flex flex-col gap-2">
        {/* Input — sempre full-width na linha 1 (desktop e mobile) */}
        <div className="w-full relative min-w-0">
          <input
            type="text"
            value={chat.inputText}
            onChange={(e) => chat.setInputText(e.target.value)}
            placeholder="Digite uma mensagem..."
            className="w-full px-4 py-3 bg-nectar-card border border-nectar-border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-brand-cyan/50 focus:ring-1 focus:ring-brand-cyan/20 transition-all duration-300"
            data-testid="message-input"
          />
        </div>

        {/* Botões — linha 2, justify-between separa esquerda de direita */}
        <div className="flex items-center justify-between">
          {/* Grupo esquerda — focus + attach */}
          <div className="flex items-center gap-2">
            <motion.button
              type="button"
              onClick={onToggleFocusMode}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 shrink-0 ${
                isFocusMode
                  ? "bg-brand-purple/20 border border-brand-purple/50 text-brand-purple"
                  : "bg-white/5 border border-nectar-border text-slate-400 hover:text-white hover:bg-white/10"
              }`}
              data-testid="focus-mode-btn"
              title={isFocusMode ? "Sair do Modo Foco" : "Modo Foco"}
            >
              {isFocusMode ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </motion.button>

            <motion.button
              type="button"
              onClick={onAttachClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-11 h-11 rounded-xl bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan hover:shadow-glow-cyan flex items-center justify-center transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              data-testid="chat-attach-image-btn"
              title="Anexar foto de cupom"
              aria-label="Anexar foto de cupom"
              disabled={chat.isLoading || chatAttachSubmitting}
            >
              <Paperclip className="w-4 h-4" />
            </motion.button>
          </div>

          {/* Grupo direita — voice + wakeword + mic + send */}
          <div className="flex items-center gap-2">
            <motion.button
              type="button"
              onClick={v.toggleVoice}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 shrink-0 ${
                v.voiceEnabled
                  ? "bg-brand-green/10 border border-brand-green/30 text-brand-green hover:shadow-glow-green"
                  : "bg-slate-800/50 border border-slate-700 text-slate-500 hover:text-slate-400"
              }`}
              data-testid="voice-toggle"
              title={v.voiceEnabled ? "Desativar voz" : "Ativar voz"}
            >
              {v.voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </motion.button>

            <motion.button
              type="button"
              onClick={v.toggleWakeWord}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 shrink-0 ${
                v.wakeWordEnabled
                  ? v.wakeWordListening
                    ? "bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 animate-pulse"
                    : "bg-brand-purple/10 border border-brand-purple/30 text-brand-purple"
                  : "bg-slate-800/50 border border-slate-700 text-slate-500 hover:text-slate-400"
              }`}
              data-testid="wakeword-toggle"
              title={v.wakeWordEnabled ? "Desativar wake word" : "Ativar wake word"}
            >
              <Headphones className="w-4 h-4" />
            </motion.button>

            <motion.button
              type="button"
              onClick={v.voiceOverlayOpen ? v.endConversation : onMicClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 shrink-0 ${
                v.voiceOverlayOpen
                  ? "bg-status-error/20 border border-status-error/50 text-status-error"
                  : "bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan hover:shadow-glow-cyan"
              }`}
              data-testid="mic-button"
            >
              {v.voiceOverlayOpen ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </motion.button>

            <motion.button
              type="submit"
              disabled={!chat.inputText.trim() || chat.isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-11 h-11 rounded-xl bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-glow-cyan transition-all duration-300 shrink-0"
              data-testid="send-button"
            >
              <Send className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </form>
    </div>
  );
}
